import { useParams } from "react-router";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useGetApiAgentsAgentId,
  usePatchApiAgentsAgentId,
} from "@/lib/api/generated/agents/agents";
import {
  postApiAiProviderConnectionsModelCatalog,
  postApiProvidersDescriptors,
  useGetApiAiProviderConnections,
} from "@/lib/api/generated/providers/providers";
import { useGetApiWorkflowDefinitions } from "@/lib/api/generated/workflows/workflows";
import {
  getApiCapabilityBindings,
  getApiCapabilityTargets,
} from "@/lib/api/generated/capabilities/capabilities";
import { useGetApiConnectorInstalls } from "@/lib/api/generated/channels/channels";
import type {
  TenantAiProviderConnectionRecordDto,
  ProviderDescriptorRecordDto,
  WorkflowDefinitionDto,
} from "@/lib/api/generated/control-api.schemas";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useAgentEditorDirty } from "@/stores/agent-editor-ui-store";
import { useAgentPolicyUiStore } from "@/stores/agent-policy-ui-store";
import type { AgentRuntimePolicy } from "../../../../../packages/shared-types/src/index.js";
import {
  type ModelSlotKey,
  type ModelSlotState,
  type PolicyFormState,
  emptyPolicy,
  policyToFormState,
  formStateToPolicy,
  collectEmbeddingModelPickerOptions,
  collectModelPickerOptions,
  readConnectionCapabilityType,
  humanizeProviderLabel,
} from "./agent-policy-utils";
import {
  applyConnectorDefaultTarget,
  applyConnectorRowEnabled,
  buildAgentPolicyViewModel,
} from "./agent-policy-view-model";

export function useAgentPolicyForm() {
  const { agentId } = useParams<{ agentId: string }>();
  const { tenantId: activeTenantId } = useTenantContext();

  // --- Data fetching (react-query) ---

  const { data: agent } = useGetApiAgentsAgentId(agentId ?? "", {
    query: { enabled: Boolean(activeTenantId && agentId) },
  });

  const { data: providerConnectionsData } = useGetApiAiProviderConnections({
    query: { enabled: Boolean(activeTenantId) },
  });
  const providerConnections = (providerConnectionsData ??
    []) as TenantAiProviderConnectionRecordDto[];

  const { data: tenantProviderModelCatalog } = useQuery({
    queryKey: queryKeys.tenantModelCatalog(activeTenantId),
    queryFn: () => postApiAiProviderConnectionsModelCatalog({ providerId: null }),
    enabled: Boolean(activeTenantId),
  });
  const { data: providerDescriptors = [] } = useQuery<ProviderDescriptorRecordDto[]>({
    queryKey: ["providers-descriptors", activeTenantId],
    queryFn: () => postApiProvidersDescriptors(null),
    enabled: Boolean(activeTenantId),
  });
  const { data: capabilityBindings } = useQuery({
    queryKey: queryKeys.tenantCapabilityBindings(activeTenantId),
    queryFn: () => getApiCapabilityBindings(),
    enabled: Boolean(activeTenantId),
  });
  const { data: capabilityTargets } = useQuery({
    queryKey: queryKeys.tenantCapabilityTargets(activeTenantId),
    queryFn: () => getApiCapabilityTargets(),
    enabled: Boolean(activeTenantId),
  });
  const { data: connectorInstalls } = useGetApiConnectorInstalls({
    query: { enabled: Boolean(activeTenantId) },
  });
  const { data: workflowDefinitions = [] } = useGetApiWorkflowDefinitions<WorkflowDefinitionDto[]>({
    query: { enabled: Boolean(activeTenantId) },
  });

  const queryClient = useQueryClient();
  const mutation = usePatchApiAgentsAgentId({
    mutation: {
      onSuccess: () => {
        if (!activeTenantId || !agentId) return;
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantAgents(activeTenantId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.agent(activeTenantId, agentId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.state });
      },
    },
  });

  // --- View mode (zustand) ---

  const { viewMode, setViewMode } = useAgentPolicyUiStore();

  // --- Form state (single useState) ---

  const [form, setForm] = useState<PolicyFormState>(() =>
    policyToFormState(emptyPolicy()),
  );

  // Sync from server when agent data arrives/changes
  useEffect(() => {
    if (!agent) return;
    setForm((prev) => {
      const next = policyToFormState((agent.runtimePolicy ?? emptyPolicy()) as AgentRuntimePolicy);
      // Preserve UI-only state that doesn't come from the server
      return { ...next, advancedOpen: prev.advancedOpen };
    });
  }, [agent]);

  // --- State updaters ---

  const updateForm = useCallback(
    <K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const updateModelSlot = useCallback(
    (slotKey: ModelSlotKey, update: Partial<ModelSlotState>) => {
      setForm((prev) => ({
        ...prev,
        modelSlots: {
          ...prev.modelSlots,
          [slotKey]: { ...prev.modelSlots[slotKey], ...update },
        },
      }));
    },
    [],
  );

  // --- Dirty tracking ---

  const serverPolicyJson = useMemo(() => {
    const p = (agent?.runtimePolicy ?? emptyPolicy()) as AgentRuntimePolicy;
    const normalized = formStateToPolicy(policyToFormState(p), { quiet: true });
    return JSON.stringify(normalized ?? emptyPolicy());
  }, [agent]);

  const currentPolicyJson = useMemo(() => {
    if (viewMode === "raw") return form.rawJson;
    const policy = formStateToPolicy(form, { quiet: true });
    return policy ? JSON.stringify(policy) : "invalid";
  }, [viewMode, form]);

  const isDirty = currentPolicyJson !== serverPolicyJson;
  useAgentEditorDirty("policy", isDirty);

  // --- Model picker options ---

  const activeProviderIds = useMemo(
    () =>
      new Set(
        providerConnections
          .filter((r) => r.status === "active")
          .map((r) => r.providerId.trim().toLowerCase()),
      ),
    [providerConnections],
  );

  const activeProviderGroups = useMemo(
    () =>
      new Set(
        providerConnections
          .filter((r) => r.status === "active")
          .map((r) => r.providerGroupId.trim().toLowerCase()),
      ),
    [providerConnections],
  );

  const modelPickerOptions = useMemo(
    () =>
      collectModelPickerOptions({
        modelConfig: (agent?.runtimePolicy as AgentRuntimePolicy | undefined)?.modelConfig,
        currentValues: Object.values(form.modelSlots).flatMap((s) => [
          s.primary,
          ...s.fallbacks,
        ]),
        modelEntriesJson: form.modelEntriesJson,
        catalogModels: tenantProviderModelCatalog?.models,
        connectedProviderIds: activeProviderIds,
        connectedProviderGroups: activeProviderGroups,
      }),
    [activeProviderGroups, activeProviderIds, agent?.runtimePolicy, form.modelEntriesJson, form.modelSlots, tenantProviderModelCatalog?.models],
  );

  const embeddingModelPickerOptions = useMemo(
    () =>
      collectEmbeddingModelPickerOptions({
        currentValue: form.memoryEmbeddingModel,
        catalogModels: tenantProviderModelCatalog?.models,
        connectedProviderIds: activeProviderIds,
        connectedProviderGroups: activeProviderGroups,
      }),
    [activeProviderGroups, activeProviderIds, form.memoryEmbeddingModel, tenantProviderModelCatalog?.models],
  );

  const connectedProviders = useMemo(
    () =>
      Array.from(
        new Set(
          providerConnections
            .filter((r) => readConnectionCapabilityType(r) === "ai_model")
            .filter((r) => r.status === "active")
            .map((r) => r.providerId.trim().toLowerCase()),
        ),
      )
        .sort((a: string, b: string) => a.localeCompare(b))
        .map((id: string) => humanizeProviderLabel(id)),
    [providerConnections],
  );

  const policyViewModel = useMemo(
    () =>
      buildAgentPolicyViewModel({
        form,
        capabilityBindings,
        capabilityTargets,
        connectorInstalls,
        providerDescriptors,
        workflowDefinitions,
      }),
    [form, capabilityBindings, capabilityTargets, connectorInstalls, providerDescriptors, workflowDefinitions],
  );

  const setConnectorRowEnabled = useCallback(
    (rowId: string, enabled: boolean) => {
      setForm((prev) => {
        return {
          ...prev,
          ...applyConnectorRowEnabled({
            form: prev,
            model: policyViewModel,
            rowId,
            enabled,
          }),
        };
      });
    },
    [policyViewModel],
  );

  const setConnectorDefaultTarget = useCallback(
    (bindingId: string, targetKey: string) => {
      setForm((prev) => {
        return {
          ...prev,
          ...applyConnectorDefaultTarget({
            model: policyViewModel,
            bindingId,
            targetKey,
          }),
        };
      });
    },
    [policyViewModel],
  );

  // --- Actions ---

  const discard = useCallback(() => {
    if (!agent) return;
    setForm((prev) => {
      const next = policyToFormState((agent.runtimePolicy ?? emptyPolicy()) as AgentRuntimePolicy);
      return { ...next, advancedOpen: prev.advancedOpen };
    });
  }, [agent]);

  const switchToRaw = useCallback(() => {
    if (viewMode === "structured") {
      const assembled = formStateToPolicy(form, { quiet: true });
      if (assembled) {
        setForm((prev) => ({ ...prev, rawJson: JSON.stringify(assembled, null, 2) }));
      }
    }
    setViewMode("raw");
  }, [viewMode, form, setViewMode]);

  const save = useCallback(async () => {
    if (!agent) return;

    let policy: AgentRuntimePolicy;

    if (viewMode === "structured") {
      const assembled = formStateToPolicy(form);
      if (!assembled) return;
      policy = assembled;
    } else {
      try {
        policy = JSON.parse(form.rawJson);
      } catch {
        toast.error("Invalid JSON");
        return;
      }
    }

    try {
      await mutation.mutateAsync({
        agentId: agentId!,
        data: {
          tenantId: activeTenantId!,
          runtimePolicy: policy as unknown as Record<string, unknown>,
        },
      });
      toast.success("Runtime policy saved");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to save runtime policy"));
    }
  }, [agent, agentId, activeTenantId, viewMode, form, mutation]);

  return {
    form,
    updateForm,
    updateModelSlot,
    isDirty,
    isPending: mutation.isPending,
    save,
    discard,
    viewMode,
    setViewMode: (mode: "structured" | "raw") => {
      if (mode === "raw") {
        switchToRaw();
      } else {
        setViewMode("structured");
      }
    },
    modelPickerOptions,
    embeddingModelPickerOptions,
    connectedProviders,
    policyViewModel,
    setConnectorRowEnabled,
    setConnectorDefaultTarget,
  };
}
